import { create } from 'zustand'
import type { CustomerType } from '@/services/authService'

const STORAGE_KEY = 'jos_auth'
const LOGIN_ID_KEY = 'jos_login_id'   // รหัสรอบการล็อกอินปัจจุบัน — เปลี่ยนทุกครั้งที่ล็อกอินใหม่ (ไม่เปลี่ยนตอนรีเฟรช token)

interface StoredAuth {
  accessToken: string
  refreshToken: string
  customerId: number
  customerType: CustomerType
}

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  customerId: number | null
  customerType: CustomerType | null
  isLoggedIn: boolean
  loginId: string | null
  markLogin: () => void
  setAuth: (accessToken: string, refreshToken: string) => void
  clearAuth: () => void
}

// JWT payload มีแค่ { customer_id, customer_type } — ไม่มี endpoint /profile ให้ดึงชื่อจริง (ดู knowledge-jos-cowork-api.md section 11)
function decodeJwtPayload(token: string): { customer_id: number; customer_type: CustomerType } | null {
  try {
    const payload = token.split('.')[1]
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    return null
  }
}

function loadStored(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const stored = loadStored()

const newLoginId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
function loadLoginId(): string | null {
  if (!stored?.accessToken) return null
  try {
    // ล็อกอินค้างอยู่ก่อนมีระบบนี้ (ยังไม่มีรหัส) → สร้างให้ตอนนี้ 1 ครั้ง
    let id = localStorage.getItem(LOGIN_ID_KEY)
    if (!id) { id = newLoginId(); localStorage.setItem(LOGIN_ID_KEY, id) }
    return id
  } catch {
    return 'session'
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: stored?.accessToken ?? null,
  refreshToken: stored?.refreshToken ?? null,
  customerId: stored?.customerId ?? null,
  customerType: stored?.customerType ?? null,
  isLoggedIn: !!stored?.accessToken,
  loginId: loadLoginId(),

  // เรียกตอน "ล็อกอินใหม่" เท่านั้น (หน้า Login) — ไม่เรียกตอนรีเฟรช token ผู้ใช้จึงไม่เห็นป๊อปอัปซ้ำเพราะ token ต่ออายุ
  markLogin: () => {
    const id = newLoginId()
    try { localStorage.setItem(LOGIN_ID_KEY, id) } catch { /* ไม่มี storage ก็ใช้ค่าในหน่วยความจำ */ }
    set({ loginId: id })
  },

  setAuth: (accessToken, refreshToken) => {
    const claims = decodeJwtPayload(accessToken)
    const next: StoredAuth = {
      accessToken,
      refreshToken,
      customerId: claims?.customer_id ?? 0,
      customerType: claims?.customer_type ?? 'INDIVIDUAL',
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    set({ ...next, isLoggedIn: true })
  },

  clearAuth: () => {
    localStorage.removeItem(STORAGE_KEY)
    try { localStorage.removeItem(LOGIN_ID_KEY) } catch { /* ข้าม */ }
    set({ accessToken: null, refreshToken: null, customerId: null, customerType: null, isLoggedIn: false, loginId: null })
  },
}))
